/**
 * Backgammon AI Player
 *
 * Three difficulty levels:
 * - Easy: random valid move sequence, 500ms delay
 * - Medium: greedy — prefer hitting, making points, advancing, 1s delay
 * - Hard: evaluation function (pip count, home board strength, blots,
 *         primes, anchor points), 2s delay
 */

import {
  PlayerSide,
  BackgammonBoard,
  BackgammonMove,
  DiceRoll,
  DieValue,
  BackgammonDifficulty,
  BackgammonGameState,
  CHECKERS_PER_PLAYER,
  P1_BAR,
  P2_BAR,
} from './types';

export class BackgammonAI {
  private difficulty: BackgammonDifficulty;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: BackgammonDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  getDelay(): number {
    switch (this.difficulty) {
      case 'easy': return 500;
      case 'medium': return 1000;
      case 'hard': return 2000;
      default: return 1000;
    }
  }

  setDifficulty(difficulty: BackgammonDifficulty): void {
    this.difficulty = difficulty;
  }

  getDifficulty(): BackgammonDifficulty {
    return this.difficulty;
  }

  /**
   * Choose the best sequence of moves for the current turn.
   * Returns an array of moves to execute in order.
   */
  chooseMoves(state: BackgammonGameState): BackgammonMove[] {
    if (!state.dice || state.validMoves.length === 0) return [];

    switch (this.difficulty) {
      case 'easy': return this.chooseMovesEasy(state);
      case 'medium': return this.chooseMovesMedium(state);
      case 'hard': return this.chooseMovesHard(state);
      default: return this.chooseMovesEasy(state);
    }
  }

  /**
   * Schedule AI moves after the configured delay.
   * Calls onMoves with the chosen move sequence.
   */
  scheduleMoves(
    state: BackgammonGameState,
    onMoves: (moves: BackgammonMove[]) => void
  ): () => void {
    this.cancelPendingMove();

    const delay = this.getDelay();
    this.pendingTimeout = setTimeout(() => {
      this.pendingTimeout = null;
      const moves = this.chooseMoves(state);
      onMoves(moves);
    }, delay);

    return () => this.cancelPendingMove();
  }

  cancelPendingMove(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  destroy(): void {
    this.cancelPendingMove();
  }

  // =========================================================================
  // Easy: Random valid moves
  // =========================================================================

  private chooseMovesEasy(state: BackgammonGameState): BackgammonMove[] {
    return this.greedyMoveSequence(state, () => Math.random());
  }

  // =========================================================================
  // Medium: Greedy — prefer hitting, making points, advancing
  // =========================================================================

  private chooseMovesMedium(state: BackgammonGameState): BackgammonMove[] {
    return this.greedyMoveSequence(state, (move: BackgammonMove) => {
      let score = 0;
      // Prefer hitting opponent
      if (move.isHit) score += 50;
      // Prefer bearing off
      if (move.isBearOff) score += 40;
      // Prefer entering from bar
      if (move.isBarEntry) score += 30;
      // Prefer making points (landing where we already have checkers)
      const board = state.board;
      const player = state.currentPlayer;
      if (!move.isBearOff) {
        const destVal = board[move.to];
        if (player === 'player1' && destVal > 0) score += 20;
        if (player === 'player2' && destVal < 0) score += 20;
      }
      // Prefer advancing toward home
      if (player === 'player1') score += (24 - move.to) * 0.5;
      else score += move.to * 0.5;
      // Small random factor
      score += Math.random() * 5;
      return score;
    });
  }

  // =========================================================================
  // Hard: Evaluation function
  // =========================================================================

  private chooseMovesHard(state: BackgammonGameState): BackgammonMove[] {
    // Generate all possible move sequences and evaluate resulting boards
    const player = state.currentPlayer;
    const sequences = this.generateAllMoveSequences(state);

    if (sequences.length === 0) return [];

    let bestSequence = sequences[0];
    let bestScore = -Infinity;

    for (const seq of sequences) {
      const resultBoard = this.simulateMoves(state.board, state.borneOff, player, seq);
      const score = this.evaluateBoard(resultBoard.board, resultBoard.borneOff, player);
      if (score > bestScore) {
        bestScore = score;
        bestSequence = seq;
      }
    }

    return bestSequence;
  }

  /**
   * Generate all possible move sequences for the current dice roll.
   * This explores the tree of moves to find all legal combinations.
   */
  private generateAllMoveSequences(state: BackgammonGameState): BackgammonMove[][] {
    const sequences: BackgammonMove[][] = [];
    const maxMoves = state.dice?.movesRemaining.length || 0;

    this.generateSequencesRecursive(
      state.board,
      state.borneOff,
      state.currentPlayer,
      [...(state.dice?.movesRemaining || [])],
      [],
      sequences,
      maxMoves
    );

    // If we have sequences of different lengths, keep only the longest
    // (must use both dice if possible)
    if (sequences.length > 0) {
      const maxLen = Math.max(...sequences.map(s => s.length));
      const longest = sequences.filter(s => s.length === maxLen);

      // If only one die can be used, must use the higher one
      if (maxLen === 1 && state.dice && !state.dice.isDoubles) {
        const higherDie = Math.max(state.dice.die1, state.dice.die2) as DieValue;
        const withHigher = longest.filter(s => s[0].dieValue === higherDie);
        if (withHigher.length > 0) return withHigher;
      }

      return longest;
    }

    return sequences;
  }

  private generateSequencesRecursive(
    board: BackgammonBoard,
    borneOff: { player1: number; player2: number },
    player: PlayerSide,
    remainingDice: DieValue[],
    currentSequence: BackgammonMove[],
    allSequences: BackgammonMove[][],
    _maxMoves: number
  ): void {
    if (remainingDice.length === 0) {
      if (currentSequence.length > 0) {
        allSequences.push([...currentSequence]);
      }
      return;
    }

    const validMoves = this.getValidMovesForBoard(board, borneOff, player, remainingDice);

    if (validMoves.length === 0) {
      if (currentSequence.length > 0) {
        allSequences.push([...currentSequence]);
      }
      return;
    }

    // Deduplicate moves to avoid exploring identical branches
    const seen = new Set<string>();
    for (const move of validMoves) {
      const key = `${move.from}-${move.to}-${move.dieValue}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const result = this.simulateMove(board, borneOff, player, move);
      const newRemaining = [...remainingDice];
      const idx = newRemaining.indexOf(move.dieValue);
      if (idx !== -1) newRemaining.splice(idx, 1);

      currentSequence.push(move);
      this.generateSequencesRecursive(
        result.board,
        result.borneOff,
        player,
        newRemaining,
        currentSequence,
        allSequences,
        _maxMoves
      );
      currentSequence.pop();
    }
  }

  /**
   * Evaluate a board position for the AI player.
   */
  private evaluateBoard(
    board: BackgammonBoard,
    borneOff: { player1: number; player2: number },
    aiPlayer: PlayerSide
  ): number {
    const opponent: PlayerSide = aiPlayer === 'player1' ? 'player2' : 'player1';
    let score = 0;

    // 1. Pip count (lower is better for AI)
    const aiPips = this.pipCount(board, borneOff, aiPlayer);
    const oppPips = this.pipCount(board, borneOff, opponent);
    score += (oppPips - aiPips) * 2;

    // 2. Borne off checkers
    score += borneOff[aiPlayer] * 30;
    score -= borneOff[opponent] * 30;

    // 3. Home board strength (made points in home board)
    score += this.homeboardStrength(board, aiPlayer) * 15;
    score -= this.homeboardStrength(board, opponent) * 15;

    // 4. Blots (exposed single checkers — bad)
    score -= this.countBlots(board, aiPlayer) * 12;
    score += this.countBlots(board, opponent) * 8;

    // 5. Primes (consecutive made points — very strong)
    score += this.longestPrime(board, aiPlayer) * 20;
    score -= this.longestPrime(board, opponent) * 20;

    // 6. Checkers on bar (very bad)
    const aiBar = aiPlayer === 'player1' ? board[P1_BAR] : Math.abs(board[P2_BAR]);
    const oppBar = opponent === 'player1' ? board[P1_BAR] : Math.abs(board[P2_BAR]);
    score -= aiBar * 25;
    score += oppBar * 20;

    // 7. Anchor points in opponent's home board
    score += this.anchorStrength(board, aiPlayer) * 10;

    return score;
  }

  private pipCount(
    board: BackgammonBoard,
    borneOff: { player1: number; player2: number },
    player: PlayerSide
  ): number {
    let pips = 0;
    if (player === 'player1') {
      pips += board[P1_BAR] * 25; // Bar checkers need 25 pips
      for (let i = 1; i <= 24; i++) {
        if (board[i] > 0) pips += board[i] * i;
      }
    } else {
      pips += Math.abs(board[P2_BAR]) * 25;
      for (let i = 1; i <= 24; i++) {
        if (board[i] < 0) pips += Math.abs(board[i]) * (25 - i);
      }
    }
    return pips;
  }

  private homeboardStrength(board: BackgammonBoard, player: PlayerSide): number {
    let madePoints = 0;
    if (player === 'player1') {
      for (let i = 1; i <= 6; i++) {
        if (board[i] >= 2) madePoints++;
      }
    } else {
      for (let i = 19; i <= 24; i++) {
        if (board[i] <= -2) madePoints++;
      }
    }
    return madePoints;
  }

  private countBlots(board: BackgammonBoard, player: PlayerSide): number {
    let blots = 0;
    for (let i = 1; i <= 24; i++) {
      if (player === 'player1' && board[i] === 1) blots++;
      if (player === 'player2' && board[i] === -1) blots++;
    }
    return blots;
  }

  private longestPrime(board: BackgammonBoard, player: PlayerSide): number {
    let longest = 0;
    let current = 0;
    for (let i = 1; i <= 24; i++) {
      if (
        (player === 'player1' && board[i] >= 2) ||
        (player === 'player2' && board[i] <= -2)
      ) {
        current++;
        if (current > longest) longest = current;
      } else {
        current = 0;
      }
    }
    return longest;
  }

  private anchorStrength(board: BackgammonBoard, player: PlayerSide): number {
    let anchors = 0;
    if (player === 'player1') {
      // Anchors in P2's home board (points 19-24)
      for (let i = 19; i <= 24; i++) {
        if (board[i] >= 2) anchors++;
      }
    } else {
      // Anchors in P1's home board (points 1-6)
      for (let i = 1; i <= 6; i++) {
        if (board[i] <= -2) anchors++;
      }
    }
    return anchors;
  }

  // =========================================================================
  // Greedy move sequence builder
  // =========================================================================

  private greedyMoveSequence(
    state: BackgammonGameState,
    scoreFn: (move: BackgammonMove) => number
  ): BackgammonMove[] {
    const moves: BackgammonMove[] = [];
    let board = [...state.board];
    let borneOff = { ...state.borneOff };
    const remaining = [...(state.dice?.movesRemaining || [])];
    const player = state.currentPlayer;

    while (remaining.length > 0) {
      const validMoves = this.getValidMovesForBoard(board, borneOff, player, remaining);
      if (validMoves.length === 0) break;

      // Score and sort
      const scored = validMoves.map(m => ({ move: m, score: scoreFn(m) }));
      scored.sort((a, b) => b.score - a.score);

      const bestMove = scored[0].move;
      moves.push(bestMove);

      // Apply move
      const result = this.simulateMove(board, borneOff, player, bestMove);
      board = result.board;
      borneOff = result.borneOff;

      // Consume die
      const idx = remaining.indexOf(bestMove.dieValue);
      if (idx !== -1) remaining.splice(idx, 1);
    }

    return moves;
  }

  // =========================================================================
  // Board simulation helpers
  // =========================================================================

  private getValidMovesForBoard(
    board: BackgammonBoard,
    borneOff: { player1: number; player2: number },
    player: PlayerSide,
    remainingDice: DieValue[]
  ): BackgammonMove[] {
    const moves: BackgammonMove[] = [];
    const barIndex = player === 'player1' ? P1_BAR : P2_BAR;
    const hasBar = player === 'player1' ? board[barIndex] > 0 : board[barIndex] < 0;
    const uniqueDice = [...new Set(remainingDice)];

    for (const die of uniqueDice) {
      if (hasBar) {
        const barMove = this.getBarEntryMoveForBoard(board, player, die);
        if (barMove) moves.push(barMove);
      } else {
        const canBear = this.canBearOffForBoard(board, player);
        for (let point = 1; point <= 24; point++) {
          if (!this.hasCheckerAtBoard(board, player, point)) continue;

          if (player === 'player1') {
            const target = point - die;
            if (target >= 1 && this.canLandOnBoard(board, player, target)) {
              moves.push({
                from: point, to: target, dieValue: die,
                isHit: board[target] < 0, isBearOff: false, isBarEntry: false,
              });
            } else if (target === 0 && canBear) {
              moves.push({
                from: point, to: 0, dieValue: die,
                isHit: false, isBearOff: true, isBarEntry: false,
              });
            } else if (target < 0 && canBear && this.isHighestInHome(board, player, point)) {
              moves.push({
                from: point, to: 0, dieValue: die,
                isHit: false, isBearOff: true, isBarEntry: false,
              });
            }
          } else {
            const target = point + die;
            if (target <= 24 && this.canLandOnBoard(board, player, target)) {
              moves.push({
                from: point, to: target, dieValue: die,
                isHit: board[target] > 0, isBearOff: false, isBarEntry: false,
              });
            } else if (target === 25 && canBear) {
              moves.push({
                from: point, to: 25, dieValue: die,
                isHit: false, isBearOff: true, isBarEntry: false,
              });
            } else if (target > 25 && canBear && this.isHighestInHome(board, player, point)) {
              moves.push({
                from: point, to: 25, dieValue: die,
                isHit: false, isBearOff: true, isBarEntry: false,
              });
            }
          }
        }
      }
    }

    return moves;
  }

  private getBarEntryMoveForBoard(
    board: BackgammonBoard,
    player: PlayerSide,
    die: DieValue
  ): BackgammonMove | null {
    if (player === 'player1') {
      const target = 25 - die;
      if (this.canLandOnBoard(board, player, target)) {
        return {
          from: P1_BAR, to: target, dieValue: die,
          isHit: board[target] < 0, isBearOff: false, isBarEntry: true,
        };
      }
    } else {
      const target = die;
      if (this.canLandOnBoard(board, player, target)) {
        return {
          from: P2_BAR, to: target, dieValue: die,
          isHit: board[target] > 0, isBearOff: false, isBarEntry: true,
        };
      }
    }
    return null;
  }

  private simulateMove(
    board: BackgammonBoard,
    borneOff: { player1: number; player2: number },
    player: PlayerSide,
    move: BackgammonMove
  ): { board: BackgammonBoard; borneOff: { player1: number; player2: number } } {
    const b = [...board];
    const bo = { ...borneOff };

    // Remove from source
    if (move.isBarEntry) {
      if (player === 'player1') b[P1_BAR]--;
      else b[P2_BAR]++;
    } else {
      if (player === 'player1') b[move.from]--;
      else b[move.from]++;
    }

    // Handle destination
    if (move.isBearOff) {
      bo[player]++;
    } else {
      if (move.isHit) {
        if (player === 'player1') {
          b[move.to] = 0;
          b[P2_BAR]--;
        } else {
          b[move.to] = 0;
          b[P1_BAR]++;
        }
      }
      if (player === 'player1') b[move.to]++;
      else b[move.to]--;
    }

    return { board: b, borneOff: bo };
  }

  private simulateMoves(
    board: BackgammonBoard,
    borneOff: { player1: number; player2: number },
    player: PlayerSide,
    moves: BackgammonMove[]
  ): { board: BackgammonBoard; borneOff: { player1: number; player2: number } } {
    let b = [...board];
    let bo = { ...borneOff };
    for (const move of moves) {
      const result = this.simulateMove(b, bo, player, move);
      b = result.board;
      bo = result.borneOff;
    }
    return { board: b, borneOff: bo };
  }

  private hasCheckerAtBoard(board: BackgammonBoard, player: PlayerSide, point: number): boolean {
    return player === 'player1' ? board[point] > 0 : board[point] < 0;
  }

  private canLandOnBoard(board: BackgammonBoard, player: PlayerSide, point: number): boolean {
    if (point < 1 || point > 24) return false;
    return player === 'player1' ? board[point] >= -1 : board[point] <= 1;
  }

  private canBearOffForBoard(board: BackgammonBoard, player: PlayerSide): boolean {
    if (player === 'player1') {
      if (board[P1_BAR] > 0) return false;
      for (let i = 7; i <= 24; i++) {
        if (board[i] > 0) return false;
      }
      return true;
    } else {
      if (board[P2_BAR] < 0) return false;
      for (let i = 1; i <= 18; i++) {
        if (board[i] < 0) return false;
      }
      return true;
    }
  }

  private isHighestInHome(board: BackgammonBoard, player: PlayerSide, point: number): boolean {
    if (player === 'player1') {
      for (let i = 6; i > point; i--) {
        if (board[i] > 0) return false;
      }
      return true;
    } else {
      for (let i = 19; i < point; i++) {
        if (board[i] < 0) return false;
      }
      return true;
    }
  }
}
